import React, { useMemo } from 'react';

import { finleyAssert } from '@finley-technologies/common';

import {
  TaskFragmentGQLType,
  useGetCurrentCreditAgreementFullArtifactQuery,
  useGetInvoiceConsolidatedDisbursementByIdQuery,
  useGetResolvedLoanByIdQuery,
} from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { parseCreditAgreementFullArtifact } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { InvoiceConsolidatedDisbursementDrawer } from 'packages/invoices/ui/consolidatedDisbursements/InvoiceConsolidatedDisbursementDrawer';
import { InvoiceDetailSideDrawer } from 'packages/invoices/ui/invoiceDetail/InvoiceDetailSideDrawer';
import { FundingRequestMultiLoanDrawer } from 'packages/loans/ui/FundingRequestDrawer';
import { LenderSharesDrawer } from 'packages/loans/ui/LenderSharesDrawer';
import { ErrorBoundary } from '~/contexts/MonitoringProvider';

const InvoiceConsolidatedDisbursementTaskDrawer = ({
  invoiceConsolidatedDisbursementId,
  creditFacilityId,
  taskId,
  onClose,
}: {
  invoiceConsolidatedDisbursementId: string;
  creditFacilityId: string;
  taskId: string;
  onClose: () => void;
}) => {
  const getConsolidatedDisbursementQuery = useGetInvoiceConsolidatedDisbursementByIdQuery(
    {
      variables: { invoiceConsolidatedDisbursementId },
    },
  );

  const currentCreditAgreementQuery = useGetCurrentCreditAgreementFullArtifactQuery(
    {
      variables: {
        creditFacilityId,
      },
    },
  );

  const facilityMechanics = useMemo(() => {
    if (
      currentCreditAgreementQuery.data &&
      currentCreditAgreementQuery.data.currentCreditAgreementFullArtifact
    ) {
      const creditAgreementString =
        currentCreditAgreementQuery.data.currentCreditAgreementFullArtifact
          .creditAgreement;

      const parsedCreditAgreement = parseCreditAgreementFullArtifact(
        creditAgreementString,
      );

      finleyAssert(
        parsedCreditAgreement,
        `Expect parsed currentCreditAgreementFullArtifact to exist for [creditFacilityId ${creditFacilityId}]`,
      );

      return parsedCreditAgreement.facilityMechanics;
    }
    return null;
  }, [creditFacilityId, currentCreditAgreementQuery.data]);

  if (getConsolidatedDisbursementQuery.error) {
    throw getConsolidatedDisbursementQuery.error;
  }

  if (currentCreditAgreementQuery.error) {
    throw currentCreditAgreementQuery.error;
  }

  if (
    !getConsolidatedDisbursementQuery.data
      ?.getInvoiceConsolidatedDisbursementById ||
    !facilityMechanics
  ) {
    return null;
  }

  return (
    <InvoiceConsolidatedDisbursementDrawer
      isOpen
      consolidatedDisbursement={
        getConsolidatedDisbursementQuery.data
          .getInvoiceConsolidatedDisbursementById
      }
      taskId={taskId}
      facilityMechanics={facilityMechanics}
      onToggle={onClose}
    />
  );
};

const LenderSharesTaskDrawer = ({
  loanId,
  fundingComponentId,
  lenderShareId,
  task,
  onClose,
}: {
  loanId: string;
  fundingComponentId: string;
  lenderShareId: string;
  task: TaskFragmentGQLType;
  onClose: () => void;
}) => {
  const getLoanQuery = useGetResolvedLoanByIdQuery({
    variables: { loanId },
  });

  if (getLoanQuery.error) {
    throw getLoanQuery.error;
  }

  const creditFacilityId = task.domainProperties.creditFacility.id;

  const currentCreditAgreementQuery = useGetCurrentCreditAgreementFullArtifactQuery(
    {
      variables: {
        creditFacilityId,
      },
    },
  );

  if (currentCreditAgreementQuery.error) {
    throw new Error(
      `Failed to get Credit Agreement for Tasks. Please contact Finley support.`,
    );
  }

  const facilityMechanics = useMemo(() => {
    if (
      currentCreditAgreementQuery.data &&
      currentCreditAgreementQuery.data.currentCreditAgreementFullArtifact
    ) {
      const creditAgreementString =
        currentCreditAgreementQuery.data.currentCreditAgreementFullArtifact
          .creditAgreement;

      const parsedCreditAgreement = parseCreditAgreementFullArtifact(
        creditAgreementString,
      );

      finleyAssert(
        parsedCreditAgreement,
        `Expect parsed currentCreditAgreementFullArtifact to exist for [creditFacilityId ${creditFacilityId}]`,
      );

      return parsedCreditAgreement.facilityMechanics;
    }
    return null;
  }, [creditFacilityId, currentCreditAgreementQuery.data]);

  if (!getLoanQuery.data?.loan || !facilityMechanics) {
    return null;
  }

  const loan = getLoanQuery.data.loan;

  const fundingComponentIds = new Set(
    loan.funding.requests
      .flatMap((r) => r.fundingComponents)
      .map((fc) => fc.fundingRequestComponentId),
  );

  if (!fundingComponentIds.has(fundingComponentId)) {
    return null;
  }

  return (
    <LenderSharesDrawer
      isOpen
      loan={loan}
      lenderShareId={lenderShareId}
      fundingComponentId={fundingComponentId}
      facilityMechanics={facilityMechanics}
      onToggle={onClose}
      taskId={task.taskEntity.id}
    />
  );
};

const TaskDetailDrawerBase = ({
  selectedTask,
  onClose,
}: {
  selectedTask: TaskFragmentGQLType;
  onClose: () => void;
}) => {
  switch (selectedTask.domainProperties.categorySpecificProperties.__typename) {
    case 'FundingRequestTaskSpecificProperties': {
      const fundingRequestId =
        selectedTask.domainProperties.categorySpecificProperties
          .fundingRequestId;

      return (
        <FundingRequestMultiLoanDrawer
          isOpen
          fundingRequestId={fundingRequestId}
          creditFacilityId={selectedTask.domainProperties.creditFacility.id}
          taskId={selectedTask.taskEntity.id}
          onToggle={onClose}
        />
      );
    }
    case 'InvoiceTaskSpecificProperties': {
      const invoiceId =
        selectedTask.domainProperties.categorySpecificProperties.invoiceId;

      return (
        <InvoiceDetailSideDrawer
          isOpen
          invoiceId={invoiceId}
          taskId={selectedTask.taskEntity.id}
          onClose={onClose}
        />
      );
    }
    case 'LenderSharesTaskSpecificProperties': {
      const loanId =
        selectedTask.domainProperties.categorySpecificProperties
          .lenderSharesLoanId;
      const lenderShareId =
        selectedTask.domainProperties.categorySpecificProperties.lenderSharesId;
      const fundingRequestComponentId =
        selectedTask.domainProperties.categorySpecificProperties
          .fundingComponentId;

      return (
        <LenderSharesTaskDrawer
          loanId={loanId}
          fundingComponentId={fundingRequestComponentId}
          lenderShareId={lenderShareId}
          task={selectedTask}
          onClose={onClose}
        />
      );
    }
    case 'ConsolidatedDisbursementTaskProperties': {
      const invoiceConsolidatedDisbursementId =
        selectedTask.domainProperties.categorySpecificProperties
          .consolidatedDisbursementId;

      return (
        <InvoiceConsolidatedDisbursementTaskDrawer
          taskId={selectedTask.taskEntity.id}
          invoiceConsolidatedDisbursementId={invoiceConsolidatedDisbursementId}
          creditFacilityId={selectedTask.domainProperties.creditFacility.id}
          onClose={onClose}
        />
      );
    }
    default: {
      throw Error(
        '[TaskDetailDrawer]: categorySpecificProperties.__typename should never be undefined',
      );
    }
  }
};

export const TaskDetailDrawer = ({
  selectedTask,
  onClose,
}: {
  selectedTask: TaskFragmentGQLType;
  onClose: () => void;
}) => {
  return (
    <ErrorBoundary
      fallback={({ showErrorToast, message }) => {
        showErrorToast(message);
        return <></>;
      }}
    >
      <TaskDetailDrawerBase selectedTask={selectedTask} onClose={onClose} />
    </ErrorBoundary>
  );
};
